import { useCallback } from "react";
import { Button, Empty, Flex, Grid } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faPaste, faPlus } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import StrictModeDroppable from "../../common/StrictModeDroppable";
import { Split } from "../../types/Programmazione";
import { useClipboard } from "../../contexts/clipboard";
import { EntityOptionsState, type ProgEditorOptions } from "../../hooks/useEntityOptions";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import type { Option } from "../../types/generics";
import SplitCardView from "./SplitCardView";
import EmptyStateCard from "../../common/EmptyStateCard";
import Title from "antd/es/typography/Title";

type Props = {
  splits: Split[];
  canEdit: boolean;
  isTargetAthlete: boolean;
  entityOpts: EntityOptionsState;
  // PROG-WEEK-PARAMS/FE-4 — bundle for the trainer-view ExerciseProgressionEditor
  // mounted inside ExerciseCardView. Optional so existing consumers / tests
  // that don't pass it keep working; the editor falls back to a free-text UI.
  progEditorOptions?: ProgEditorOptions;
  mutations: ReturnType<typeof useProgrammazioneMutations>;
  exercisesOptions: Option[];
  muscleGroupOptions: Option[];
};

export default function SplitListView({
  splits,
  canEdit,
  isTargetAthlete,
  entityOpts,
  progEditorOptions,
  mutations,
  exercisesOptions,
  muscleGroupOptions,
}: Props) {
  const { copiedSplit } = useClipboard();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const showIndexChips = !!screens.md && splits && splits.length > 1;

  const handleSplitDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;
      mutations.reorderSplits(result.source.index, result.destination.index);
    },
    [mutations]
  );

  if (!splits || splits.length === 0) {
    if (canEdit) {
      return (
        <EmptyStateCard
          icon={faLayerGroup}
          title="Nessuno Split"
          body="Questa programmazione non ha ancora split definiti."
          primaryCta={{
            label: "Aggiungi Split",
            onClick: () => mutations.addSplit(),
            icon: faPlus,
          }}
          secondaryCta={
            copiedSplit
              ? {
                  label: "Incolla Split",
                  onClick: () => mutations.pasteSplit(copiedSplit),
                  icon: faPaste,
                }
              : undefined
          }
        />
      );
    }
    return (
      <Empty
        image={
          <FontAwesomeIcon
            icon={faLayerGroup}
            style={{ fontSize: 32, color: "var(--app-gray-400)" }}
          />
        }
        description={
          <Flex vertical gap={4} align="center" style={{ paddingTop: 8 }}>
            <strong style={{ fontSize: 14 }}>Nessuno Split</strong>
            <span style={{ color: "var(--app-gray-600)", fontSize: 13 }}>
              Questa programmazione non ha ancora split definiti.
            </span>
          </Flex>
        }
      />
    );
  }

  const splitCards = splits.map((split, index) =>
    canEdit ? (
      <Draggable key={`view-split-${index}`} draggableId={`view-split-${index}`} index={index}>
        {(dragProvided) => (
          <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
            <SplitCardView
              split={split}
              splitIndex={index}
              canEdit={canEdit}
              isTargetAthlete={isTargetAthlete}
              entityOpts={entityOpts}
              progEditorOptions={progEditorOptions}
              mutations={mutations}
              exercisesOptions={exercisesOptions}
              muscleGroupOptions={muscleGroupOptions}
              dragHandleProps={dragProvided.dragHandleProps}
            />
          </div>
        )}
      </Draggable>
    ) : (
      <SplitCardView
        key={`view-split-${index}`}
        split={split}
        splitIndex={index}
        canEdit={canEdit}
        isTargetAthlete={isTargetAthlete}
        entityOpts={entityOpts}
        progEditorOptions={progEditorOptions}
        mutations={mutations}
        exercisesOptions={exercisesOptions}
        muscleGroupOptions={muscleGroupOptions}
      />
    )
  );

  return (
    <>
      {/* PROG-DETAIL/IMPL H1 — count chip beside title. H2 — top CTA row
          so the user doesn't have to scroll to the bottom to add. Mirrors
          ProgrammazioneForm's splits-workspace header. */}
      <Flex
        align="center"
        gap={12}
        wrap="wrap"
        className="mb-3"
        justify="space-between"
      >
        <Flex align="center" gap={12} wrap="wrap">
          <Title level={3} className="m-0">
            Splits e Esercizi
          </Title>
          <span className="cm-tag cm-tag--brand">
            {splits.length} Split
          </span>
        </Flex>
        {canEdit && (
          <Flex align="center" gap={8} wrap="wrap">
            <Button
              type="default"
              className="cm-btn cm-btn--primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => mutations.addSplit()}
              block={isMobile}
            >
              Aggiungi Split
            </Button>
            {copiedSplit && (
              <Button
                type="default"
                className="cm-btn cm-btn--ghost"
                icon={<FontAwesomeIcon icon={faPaste} />}
                onClick={() => mutations.pasteSplit(copiedSplit)}
                block={isMobile}
              >
                Incolla Split
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      {showIndexChips && (
        <Flex
          gap={6}
          wrap="wrap"
          className="mb-3"
          style={{ paddingTop: 8, borderTop: "1px dashed var(--app-gray-100)" }}
        >
          {splits.map((s, idx) => (
            <span
              key={`view-splits-index-${idx}`}
              className="cm-tag cm-tag--neutral cm-tag--clickable"
              onClick={() => {
                const el = document.getElementById(`split-card-${idx}`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              {s.dayName || `Split ${idx + 1}`} · {s.exercises?.length || 0} es.
            </span>
          ))}
        </Flex>
      )}

      {canEdit ? (
        <DragDropContext onDragEnd={handleSplitDragEnd}>
          <StrictModeDroppable droppableId="view-splits">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {splitCards}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      ) : (
        <div>{splitCards}</div>
      )}
    </>
  );
}
